import type { ParameterTimeSeriesChartResult } from "@/lib/api";

import { resolveSeriesDisplayLabel } from "./chart-series-presentation";
import type { ChartTimeBounds } from "./get-shared-chart-time-bounds";
import type { PasquillTimeSeriesConfig } from "./graph.types";
import { pasquillClassLetter, type PasquillClassLetter } from "./pasquill-classes";

/** One run of a single class, drawn as one block spanning `start` to `end`. */
export type PasquillSegment = {
  letter: PasquillClassLetter;
  /** Epoch ms of the run's first datapoint. */
  start: number;
  /** Epoch ms where the run stops: the next datapoint's time (exclusive). */
  end: number;
};

export type PasquillTimelineRow = {
  parameterId: string;
  label: string;
  segments: PasquillSegment[];
};

export type PasquillTimeline = {
  rows: PasquillTimelineRow[];
  /**
   * Spacing of the underlying datapoints. The chart feeds this to the x-axis as
   * the series point range: a column series derives the same value from its data
   * and pads the axis by half of it, so without this the timeline's ticks would
   * sit at different pixels than the bars in the chart above.
   */
  stepMs: number;
};

const ONE_HOUR_MS = 60 * 60 * 1000;

/**
 * Median gap between datapoints, used to give the final block a width: every
 * other block ends where the next one begins, but the last has nothing to
 * lean on. Median rather than mean so one missing step does not skew it.
 */
export function inferStepMs(times: number[], fallback = ONE_HOUR_MS): number {
  const deltas: number[] = [];
  for (let index = 1; index < times.length; index++) {
    const delta = times[index] - times[index - 1];
    if (delta > 0) deltas.push(delta);
  }
  if (deltas.length === 0) return fallback;
  deltas.sort((a, b) => a - b);
  return deltas[Math.floor(deltas.length / 2)];
}

/**
 * Collapses datapoints into runs of one class so a class holding for six hours
 * becomes a single block. Missing or unmappable values break a run rather than
 * being bridged, so a block never claims a span the series has no data for.
 */
export function mergeIntoSegments(
  points: { time: number; letter: PasquillClassLetter | null }[],
  stepMs: number,
): PasquillSegment[] {
  const segments: PasquillSegment[] = [];

  for (const [index, { time, letter }] of points.entries()) {
    if (letter === null) continue;
    const end = points[index + 1]?.time ?? time + stepMs;
    const previous = segments.at(-1);
    // Only extend across an adjacent step; a gap in between ends the run.
    if (previous && previous.letter === letter && previous.end === time) {
      previous.end = end;
      continue;
    }
    segments.push({ letter, start: time, end });
  }

  return segments;
}

/**
 * Builds one row of class blocks per configured series.
 *
 * `timeBounds` is the window the sibling charts put on their x-axis. Blocks
 * starting before it are clamped, but their `end` is left alone: the axis
 * clips the overhang, and clipping it here would erase the final block
 * whenever the bounds end exactly on the last datapoint.
 */
export function buildPasquillTimeline(
  config: PasquillTimeSeriesConfig,
  data: ParameterTimeSeriesChartResult[],
  timeBounds?: ChartTimeBounds,
): PasquillTimeline {
  const resultsByParameter = new Map(data.map((result) => [result.parameter.id, result]));

  const pointsBySeries = config.series.map((seriesConfig) => {
    const result = resultsByParameter.get(seriesConfig.parameter_id);
    return {
      seriesConfig,
      result,
      points: (result?.data?.datapoints ?? [])
        .map((datapoint) => ({
          time: +new Date(datapoint.timestamp),
          letter: pasquillClassLetter(datapoint.value),
        }))
        .filter((point) => !Number.isNaN(point.time))
        .sort((a, b) => a.time - b.time),
    };
  });

  // One step for the whole chart: the axis has a single point range, and
  // aligned series share a timestamp grid so the union measures the same gap.
  const stepMs = inferStepMs(
    [
      ...new Set(pointsBySeries.flatMap(({ points }) => points.map((point) => point.time))),
    ].sort((a, b) => a - b),
  );

  return {
    stepMs,
    rows: pointsBySeries.map(({ seriesConfig, result, points }) => {
      const segments = mergeIntoSegments(points, stepMs);

      return {
        parameterId: seriesConfig.parameter_id,
        label: resolveSeriesDisplayLabel(seriesConfig, result),
        segments:
          timeBounds === undefined
            ? segments
            : segments
                .filter(
                  (segment) =>
                    segment.end > timeBounds.min && segment.start <= timeBounds.max,
                )
                .map((segment) => ({
                  ...segment,
                  start: Math.max(segment.start, timeBounds.min),
                })),
      };
    }),
  };
}
